import api from '@/services/api'
import { logError } from '@/utils/sentry'
import { createPersistedStore } from './baseStore'
import type { BaseStateWithLoadingAndError } from './baseStore'

interface RegistrationOptionsState extends BaseStateWithLoadingAndError {
  currencies: string[]
  budgets: string[]
  companySizes: string[]
  loading: boolean
  error: string | string[] | null
  isInitialized: boolean
}

interface RegistrationOptionsActions {
  initialize: () => Promise<void>
  resetInitialization: () => void
}

type SetState<T> = (state: T | Partial<T> | ((state: T) => T | Partial<T>), replace?: boolean) => void

type GetState<T> = () => T

const initialState: RegistrationOptionsState = {
  currencies: [],
  budgets: [],
  companySizes: [],
  loading: false,
  error: null,
  isInitialized: false,
}

const actions = (
  set: SetState<RegistrationOptionsState>,
  get: GetState<RegistrationOptionsState & RegistrationOptionsActions>,
): RegistrationOptionsActions => ({
  resetInitialization: () => {
    set((state: RegistrationOptionsState) => ({ ...state, isInitialized: false }))
  },
  initialize: async () => {
    const state = get()
    if (state.isInitialized) return

    try {
      set({ loading: true, error: null })
      const [currenciesRes, budgetsRes, companySizesRes] = await Promise.all([
        api.get('/currencies'),
        api.get('/budgets'),
        api.get('/company_sizes'),
      ])

      const normalizeList = (res: unknown): string[] => {
        const r = res as { data?: unknown; items?: unknown }
        const data = (r as { data?: unknown }).data as { items?: string[] } | string[] | { data?: { items?: string[] } } | undefined
        if (Array.isArray(r)) return r as string[]
        if (Array.isArray((r as { items?: unknown }).items)) return (r as { items: string[] }).items
        if (Array.isArray(data)) return data as string[]
        // @ts-expect-error – best effort parsing
        if (Array.isArray(data?.items)) return data.items as string[]
        if (Array.isArray(res)) return res as string[]
        return []
      }

      const currencies = normalizeList(currenciesRes?.data ?? currenciesRes) || []
      const budgets = normalizeList(budgetsRes?.data ?? budgetsRes) || []
      const companySizes = normalizeList(companySizesRes?.data ?? companySizesRes) || []

      set({
        currencies,
        budgets,
        companySizes,
        loading: false,
        isInitialized: true,
      })
    } catch (error) {
      logError('Failed to initialize registration options', { error })
      set({ loading: false, error: 'Failed to load options' })
    }
  },
})

const useRegistrationOptionsStore = createPersistedStore<RegistrationOptionsState, RegistrationOptionsActions>(
  'registration-options-store',
  initialState,
  actions,
)

export default useRegistrationOptionsStore
